/**
 * The contributor-entered GitHub references, with the reminder that nothing
 * here is verified by an integration.
 */

import type { ReactNode } from 'react';

import type { TaskDetail } from '../../api.js';
import { useWorkspaceCopy } from '../../components.js';
import { Tag } from '../../ui.js';
import { SectionHeading } from '../common.js';

export function TaskReferencesSection({
  task,
}: {
  readonly task: TaskDetail;
}): ReactNode {
  const copy = useWorkspaceCopy();

  return (
    <section
      aria-label={copy.task.githubReferences}
      style={{ marginTop: 'var(--space-lg)' }}
    >
      <SectionHeading>{copy.task.githubReferences}</SectionHeading>
      <p
        style={{
          font: 'var(--type-caption)',
          color: 'var(--text-muted)',
          marginBottom: 8,
        }}
      >
        {copy.task.githubUnverified}
      </p>
      {task.references.length === 0 ? (
        <p style={{ font: 'var(--type-body-md)', color: 'var(--text-muted)' }}>
          {copy.task.noReferences}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {task.references.map((reference) => (
            <li key={reference.id} style={{ marginBottom: 8 }}>
              <Tag tone="neutral">
                {copy.task.referenceKinds[reference.kind] ?? reference.kind}
              </Tag>{' '}
              <a
                className="mj-ltr"
                dir="ltr"
                href={reference.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  font: 'var(--type-body-tabular)',
                  color: 'var(--text-link)',
                  overflowWrap: 'anywhere',
                }}
              >
                {reference.url}
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
